import { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';

const TOTAL_SECTIONS = 9;

const COLORS = {
  navy: '#0d1f4e', green: '#0f7a62', greenBright: '#22c57a',
  bg: '#f5f7fb', white: '#ffffff', border: '#dbe0ed',
  textMuted: '#8a93b0', textBody: '#4a5578',
};

const INDUSTRIES = [
  'Fitness & Recreation', 'Healthcare', 'Restaurant & Food Service',
  'Retail', 'Hospitality & Hotels', 'Education', 'Non-Profit', 'Government', 'Other',
];

const CADENCES = [
  { value: 'weekly', label: 'Weekly', description: 'Every 7 days' },
  { value: 'biweekly', label: 'Bi-Weekly', description: 'Every 14 days' },
  { value: 'monthly', label: 'Monthly', description: 'Every month' },
  { value: 'semester', label: 'Semester', description: 'Every semester' },
  { value: 'quarterly', label: 'Quarterly', description: 'Every quarter' },
  { value: 'custom', label: 'Custom', description: 'Define your own cycle' },
];

const NOTICE_OPTIONS = [
  { value: 'none', label: 'No requirement', description: 'Publish anytime' },
  { value: '24', label: '24 hours', description: 'At least 24hrs in advance' },
  { value: '48', label: '48 hours', description: 'At least 48hrs in advance' },
  { value: '72', label: '72 hours', description: 'At least 72hrs in advance' },
  { value: '1week', label: '1 week', description: 'At least 1 week in advance' },
  { value: '2weeks', label: '2 weeks', description: 'At least 2 weeks in advance' },
];

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
];

const US_STATES = [
  { abbr: 'AL', name: 'Alabama' }, { abbr: 'AK', name: 'Alaska' },
  { abbr: 'AZ', name: 'Arizona' }, { abbr: 'AR', name: 'Arkansas' },
  { abbr: 'CA', name: 'California' }, { abbr: 'CO', name: 'Colorado' },
  { abbr: 'CT', name: 'Connecticut' }, { abbr: 'DE', name: 'Delaware' },
  { abbr: 'FL', name: 'Florida' }, { abbr: 'GA', name: 'Georgia' },
  { abbr: 'HI', name: 'Hawaii' }, { abbr: 'ID', name: 'Idaho' },
  { abbr: 'IL', name: 'Illinois' }, { abbr: 'IN', name: 'Indiana' },
  { abbr: 'IA', name: 'Iowa' }, { abbr: 'KS', name: 'Kansas' },
  { abbr: 'KY', name: 'Kentucky' }, { abbr: 'LA', name: 'Louisiana' },
  { abbr: 'ME', name: 'Maine' }, { abbr: 'MD', name: 'Maryland' },
  { abbr: 'MA', name: 'Massachusetts' }, { abbr: 'MI', name: 'Michigan' },
  { abbr: 'MN', name: 'Minnesota' }, { abbr: 'MS', name: 'Mississippi' },
  { abbr: 'MO', name: 'Missouri' }, { abbr: 'MT', name: 'Montana' },
  { abbr: 'NE', name: 'Nebraska' }, { abbr: 'NV', name: 'Nevada' },
  { abbr: 'NH', name: 'New Hampshire' }, { abbr: 'NJ', name: 'New Jersey' },
  { abbr: 'NM', name: 'New Mexico' }, { abbr: 'NY', name: 'New York' },
  { abbr: 'NC', name: 'North Carolina' }, { abbr: 'ND', name: 'North Dakota' },
  { abbr: 'OH', name: 'Ohio' }, { abbr: 'OK', name: 'Oklahoma' },
  { abbr: 'OR', name: 'Oregon' }, { abbr: 'PA', name: 'Pennsylvania' },
  { abbr: 'RI', name: 'Rhode Island' }, { abbr: 'SC', name: 'South Carolina' },
  { abbr: 'SD', name: 'South Dakota' }, { abbr: 'TN', name: 'Tennessee' },
  { abbr: 'TX', name: 'Texas' }, { abbr: 'UT', name: 'Utah' },
  { abbr: 'VT', name: 'Vermont' }, { abbr: 'VA', name: 'Virginia' },
  { abbr: 'WA', name: 'Washington' }, { abbr: 'WV', name: 'West Virginia' },
  { abbr: 'WI', name: 'Wisconsin' }, { abbr: 'WY', name: 'Wyoming' },
  { abbr: 'DC', name: 'Washington D.C.' },
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const INDUSTRY_ROLES = {
  'Fitness & Recreation': ['Manager', 'Fitness Instructor', 'Lifeguard', 'Front Desk', 'Personal Trainer', 'On-Call'],
  'Healthcare': ['Manager', 'Nurse', 'Doctor', 'Receptionist', 'Medical Assistant', 'On-Call'],
  'Restaurant & Food Service': ['Manager', 'Server', 'Host', 'Bartender', 'Cook', 'Dishwasher', 'On-Call'],
  'Retail': ['Manager', 'Sales Associate', 'Cashier', 'Stock Associate', 'Supervisor', 'On-Call'],
  'Hospitality & Hotels': ['Manager', 'Front Desk', 'Concierge', 'Housekeeping', 'Bellhop', 'On-Call'],
  'Education': ['Administrator', 'Teacher', "Teacher's Aide", 'Counselor', 'Supervisor', 'On-Call'],
  'Non-Profit': ['Director', 'Coordinator', 'Volunteer Manager', 'Staff', 'On-Call'],
  'Government': ['Director', 'Supervisor', 'Staff', 'Part-Time Staff', 'On-Call'],
  'Other': ['Manager', 'Supervisor', 'Full-Time Staff', 'Part-Time Staff', 'On-Call'],
};

// Helpers
function titleCase(str) { return str.replace(/\b\w/g, c => c.toUpperCase()); }

function calcHours(start, end) {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 1440;
  const h = mins / 60;
  return h % 1 === 0 ? String(h) : h.toFixed(2);
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function to24(timeStr) {
  if (!timeStr) return '';
  const [time, meridiem] = timeStr.split(' ');
  let [h, m] = time.split(':').map(Number);
  if (meridiem === 'PM' && h !== 12) h += 12;
  if (meridiem === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function getSuggestedRoles(industries) {
  return [...new Set(industries.flatMap(ind => INDUSTRY_ROLES[ind] || INDUSTRY_ROLES['Other']))];
}

// Shared styles
const inputStyle = {
  width: '100%', padding: '10px 14px', border: `1px solid ${COLORS.border}`,
  borderRadius: 8, fontSize: 14, color: COLORS.navy,
  fontFamily: 'Inter, sans-serif', outline: 'none', background: '#fff', boxSizing: 'border-box',
};
const labelStyle = { fontSize: 13, fontWeight: 700, color: COLORS.navy, display: 'block', marginBottom: 6 };
const hintStyle = { fontSize: 12, color: COLORS.textMuted, marginTop: 4 };

// Reusable components
function Toggle({ value, onChange }) {
  return (
    <button onClick={() => onChange(!value)} style={{ width: 48, height: 26, borderRadius: 100, border: 'none', cursor: 'pointer', flexShrink: 0, background: value ? COLORS.greenBright : COLORS.border, position: 'relative', transition: 'background 0.2s' }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: value ? 25 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  );
}

function ToggleRow({ label, description, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderTop: `1px solid ${COLORS.border}` }}>
      <div style={{ flex: 1, paddingRight: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.navy }}>{label}</div>
        {description && <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{description}</div>}
      </div>
      <Toggle value={value} onChange={onChange} />
    </div>
  );
}

function RuleCard({ title, description, children }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ background: COLORS.navy, padding: '12px 20px' }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{title}</div>
        {description && <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 2 }}>{description}</div>}
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  );
}

function PctPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
      {[70, 80, 90].map(pct => (
        <button key={pct} onClick={() => onChange(pct)} style={{ padding: '7px 18px', borderRadius: 8, cursor: 'pointer', border: `2px solid ${value === pct ? COLORS.greenBright : COLORS.border}`, background: value === pct ? 'rgba(34,197,122,0.08)' : '#fff', fontSize: 13, fontWeight: value === pct ? 700 : 400 }}>{pct}%</button>
      ))}
    </div>
  );
}

function ModeToggle({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 28 }}>
      {options.map(m => (
        <button key={m.value} onClick={() => onChange(m.value)} style={{ flex: 1, padding: '14px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${value === m.value ? COLORS.greenBright : COLORS.border}`, background: value === m.value ? 'rgba(34,197,122,0.08)' : '#fff' }}>
          <div style={{ fontSize: 20, marginBottom: 4 }}>{m.icon}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>{m.label}</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{m.desc}</div>
        </button>
      ))}
    </div>
  );
}

function NavButtons({ onBack, onNext, nextLabel, saving, disabled }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
      {onBack ? (
        <button onClick={onBack} style={{ padding: '14px 32px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${COLORS.border}`, background: '#fff', fontSize: 15, fontWeight: 700, color: COLORS.navy }}>← Back</button>
      ) : <div />}
      <button onClick={onNext} disabled={saving || disabled} style={{ padding: '14px 40px', borderRadius: 10, border: 'none', background: disabled ? COLORS.textMuted : COLORS.greenBright, color: COLORS.navy, fontSize: 15, fontWeight: 800, cursor: (saving || disabled) ? 'not-allowed' : 'pointer' }}>
        {saving ? 'Saving...' : nextLabel}
      </button>
    </div>
  );
}

function ProgressBar({ section }) {
  const labels = ['Basics', 'Roles', 'Shifts', 'Staff', 'Rules', 'Access', 'Notifications', 'Coverage', 'Review'];
  return (
    <div style={{ background: '#fff', borderBottom: `1px solid ${COLORS.border}`, padding: '16px 32px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>Step {section} of {TOTAL_SECTIONS}</span>
          <span style={{ fontSize: 13, color: COLORS.textMuted }}>{Math.round((section / TOTAL_SECTIONS) * 100)}% complete</span>
        </div>
        <div style={{ background: COLORS.border, borderRadius: 100, height: 6, overflow: 'hidden' }}>
          <div style={{ background: COLORS.greenBright, height: '100%', width: `${(section / TOTAL_SECTIONS) * 100}%`, borderRadius: 100, transition: 'width 0.3s' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          {labels.map((label, i) => (
            <span key={label} style={{ fontSize: 10, color: i + 1 === section ? COLORS.greenBright : i + 1 < section ? COLORS.green : COLORS.textMuted, fontWeight: i + 1 === section ? 700 : 400 }}>{label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Main component
export default function OrgSetupWizard({ onComplete }) {
  const [section, setSection] = useState(1);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [finishError, setFinishError] = useState('');

  // S1
  const [orgName, setOrgName] = useState('');
  const [industry, setIndustry] = useState([]);
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [cadence, setCadence] = useState('weekly');
  const [customDays, setCustomDays] = useState('');
  const [minNotice, setMinNotice] = useState('none');
  const [holidays, setHolidays] = useState(false);

  // S2
  const [wizardRoles, setWizardRoles] = useState([]);
  const [newRoleName, setNewRoleName] = useState('');

  // S3
  const [shiftMode, setShiftMode] = useState('manual');
  const [wizardShifts, setWizardShifts] = useState([]);
  const [activeDay, setActiveDay] = useState('Sunday');
  const [newShift, setNewShift] = useState({ startTime: '', endTime: '', hours: '', peopleNeeded: 1, certifications: '' });

  // S4
  const [staffMode, setStaffMode] = useState('manual');
  const [wizardStaff, setWizardStaff] = useState([]);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ name: '', role: '', email: '', phone: '', max_hours: '', priority: 'Standard', available_days: '', preferred_shifts: '', certifications: '' });

  // S5
  const [rules, setRules] = useState({
    orgMaxHoursWeek: '', orgMaxHoursMonth: '', orgMaxHoursPeriod: '',
    blockOnBudgetExceeded: false, budgetWarnPct: 80,
    minShiftHours: 2, empWarnPct: 80,
    certMatching: true, respectPriority: true,
    clopenDetection: true, minRestHours: 8, clopenHardBlock: false,
  });

  // S6
  const [access, setAccess] = useState({
    // Manager Access
    managersCanInviteManagers: false,
    requireOwnerApprovalToPublish: false,
    managersCanEditRules: true,
    managerDefaults: {
      editSchedules: true,
      approveTimeOff: true,
      approveSwaps: true,
      manageEmployees: true,
      editOrgRules: true,
      viewLaborCosts: true,
    },
    // Employee Access
    empViewOwnSchedule: true,
    empSubmitAvailability: true,
    empRequestTimeOff: true,
    empRequestSwaps: true,
    empClaimOpenShifts: true,
    empViewCoworkerSchedules: true,
    empUpdateOwnInfo: true,
    // Workflows
    swapModel: 'auto_with_notify',
    timeOffModel: 'manager_approve_all',
  });

  // S7
  const [notifs, setNotifs] = useState({
    // Channel defaults
    channelDefaults: {
      push: true,
      email: false,
      sms: false,
    },
    // Notification events (org enables/disables capability per event)
    events: {
      schedulePublished: true,
      shiftAssignedOrChanged: true,
      shiftReminder: true,
      swapRequestReceived: true,
      timeOffStatusUpdate: true,
      openShiftAvailable: true,
      mgrTimeOffSubmitted: true,
      mgrScheduleNeedsReview: true,
      mgrBudgetWarning: true,
    },
    // Whether employees can customize their own preferences
    allowEmployeeCustomization: true,
    // First-login behavior
    onboardingMode: 'banner', // 'banner' | 'forced' | 'silent' | 'external'
  });

  // S8
  const [coverage, setCoverage] = useState({
    // Gap behavior — what happens when a shift can't be filled
    // NOTE: 'auto_assign' is intentionally NOT an option per product principle
    gapMode: 'open_shift', // 'open_shift' | 'alert_manager' | 'leave_open' | 'configurable'
    // Soft reminders — org-defined, no compliance promises
    requiredRoles: [], // array of role names that should always be on shift
    minTotalEnabled: false,
    minTotalCount: '',
    // Coverage patterns — peak/slow day flags
    patternsEnabled: false,
    peakDays: [], // ['Friday', 'Saturday']
    slowDays: [],
  });

  // Auth-derived org.
  // Resolved on mount from the org this user owns; if they don't have one yet,
  // it's created in saveS1 and the new id is captured here. Every saveSx writes
  // to this id instead of a hardcoded constant.
  const [orgId, setOrgId] = useState(null);

  useEffect(() => {
    async function loadOrg() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Pull the full org row (not just id) so we can rehydrate the form.
      const { data: org } = await supabase
        .from('organizations')
        .select('*')
        .eq('owner_id', user.id)
        .limit(1)
        .maybeSingle();

      if (!org) return; // brand-new user, no saved org — start fresh at Section 1.

      setOrgId(org.id);

      // ── Section 1: basics ──
      if (org.name) setOrgName(org.name);
      if (org.industry) setIndustry(org.industry);
      if (org.city) setCity(org.city);
      if (org.state) setState(org.state);
      if (org.timezone) setTimezone(org.timezone);
      if (org.min_notice) setMinNotice(org.min_notice);
      if (typeof org.track_holidays === 'boolean') setHolidays(org.track_holidays);
      if (org.schedule_cadence) {
        if (org.schedule_cadence.startsWith('custom_')) {
          setCadence('custom');
          setCustomDays(org.schedule_cadence.replace('custom_', ''));
        } else {
          setCadence(org.schedule_cadence);
        }
      }

      // ── Sections 5–8: settings blobs (guard against null) ──
      if (org.scheduling_rules) setRules(r => ({ ...r, ...org.scheduling_rules }));
      if (org.access_settings) setAccess(a => ({ ...a, ...org.access_settings }));
      if (org.notification_settings) setNotifs(n => ({ ...n, ...org.notification_settings }));
      if (org.coverage_settings) setCoverage(c => ({ ...c, ...org.coverage_settings }));

      // ── Section 2: roles ──
      const { data: rolesData } = await supabase
        .from('roles').select('*').eq('org_id', org.id);
      if (rolesData && rolesData.length) {
        setWizardRoles(rolesData.map(r => ({ id: r.id, name: r.name })));
      }

      // ── Section 3: shifts ──
      // Prefer the clean start_time/end_time columns. Fall back to parsing the
      // legacy shift_time string for older rows saved before those columns existed.
      const { data: shiftsData } = await supabase
        .from('shifts').select('*').eq('org_id', org.id);
      if (shiftsData && shiftsData.length) {
        setWizardShifts(shiftsData.map(s => {
          let startTime = s.start_time || '';
          let endTime = s.end_time || '';
          if (!startTime && s.shift_time) {
            const [startRaw, endRaw] = s.shift_time.split(' - ');
            startTime = startRaw ? to24(startRaw.trim()) : '';
            endTime = endRaw ? to24(endRaw.trim()) : '';
          }
          return {
            id: s.id,
            day: s.day,
            startTime,
            endTime,
            hours: s.hours,
            peopleNeeded: s.people_needed || 1,
            certifications: s.certifications || '',
          };
        }));
      }

      // ── Section 4: staff ──
      const { data: staffData } = await supabase
        .from('employees').select('*').eq('org_id', org.id);
      if (staffData && staffData.length) {
        setWizardStaff(staffData.map(e => ({
          id: e.id,
          name: e.name || '',
          role: e.role || '',
          email: e.email || '',
          phone: e.phone || '',
          max_hours: e.max_hours || '',
          priority: e.priority || 'Standard',
          available_days: e.available_days || '',
          preferred_shifts: e.preferred_shifts || '',
          certifications: e.certifications || '',
        })));
      }

      // ── Finally: jump to where they left off ──
      if (org.current_section) setSection(org.current_section);
    }
    loadOrg();
  }, []);

  async function persistSection(sectionNum) {
    if (!orgId) return;
    await supabase
      .from('organizations')
      .update({ current_section: sectionNum })
      .eq('id', orgId);
  }

  function validateS1() {
    const e = {};
    if (!orgName.trim()) e.orgName = 'Organization name is required';
    if (!industry.length) e.industry = 'Please select at least one industry';
    if (!city.trim()) e.city = 'City is required';
    if (!state) e.state = 'State is required';
    if (cadence === 'custom' && !customDays) e.customDays = 'Please enter your cycle length';
    setErrors(e);
    return !Object.keys(e).length;
  }

 async function saveS1() {
    if (!validateS1()) return;
    setSaving(true);

    const orgFields = {
      name: orgName,
      industry,
      city,
      state,
      timezone,
      schedule_cadence: cadence === 'custom' ? `custom_${customDays}` : cadence,
      min_notice: minNotice,
      track_holidays: holidays,
      current_section: 2,
    };

    let activeOrgId = orgId;

    if (activeOrgId) {
      await supabase.from('organizations').update(orgFields).eq('id', activeOrgId);
    } else {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('organizations')
        .insert({ ...orgFields, owner_id: user.id })
        .select('id')
        .single();
      if (error || !data) {
        setSaving(false);
        alert('Could not create your organization. Please try again.');
        return;
      }
      activeOrgId = data.id;
      setOrgId(data.id);
    }

    setSaving(false);
    setSection(2);
  }

async function saveS2() {
    if (!wizardRoles.length) return;
    setSaving(true);
    await supabase.from('roles').delete().eq('org_id', orgId);
    await supabase.from('roles').insert(wizardRoles.map(r => ({ org_id: orgId, name: r.name })));
    await persistSection(3);
    setSaving(false);
    setSection(3);
  }

 async function saveS3() {
    if (!wizardShifts.length) { alert('Please add at least one shift.'); return; }
    setSaving(true);
    await supabase.from('shifts').delete().eq('org_id', orgId);
    await supabase.from('shifts').insert(wizardShifts.map(s => ({ org_id: orgId, day: s.day, start_time: s.startTime, end_time: s.endTime, hours: s.hours, people_needed: s.peopleNeeded, certifications: s.certifications })));
    setSaving(false);
    setSection(4);
  }

 async function saveS4() {
    setSaving(true);

    // Employee IDs are referenced by availability_tokens/submissions, so they must stay
    // stable across re-saves — no delete-all-then-reinsert here. Diff wizardStaff against
    // the rows already in the DB: update existing rows in place (matched by real DB id),
    // insert brand-new rows, and delete rows the user removed. Locally-added rows carry a
    // fake Date.now() id, so we key off the actual DB ids rather than id presence.
    const { data: existingRows } = await supabase
      .from('employees').select('id').eq('org_id', orgId);
    const existingIds = new Set((existingRows || []).map(r => r.id));

    const fields = e => ({
      name: e.name, role: e.role, email: e.email, phone: e.phone,
      max_hours: e.max_hours || null, priority: e.priority || 'Standard',
      available_days: e.available_days, preferred_shifts: e.preferred_shifts,
      certifications: e.certifications,
    });

    const toInsert = wizardStaff.filter(e => !existingIds.has(e.id));
    const keptIds = new Set(wizardStaff.map(e => e.id));
    const toDelete = [...existingIds].filter(id => !keptIds.has(id));

    for (const e of wizardStaff) {
      if (existingIds.has(e.id)) await supabase.from('employees').update(fields(e)).eq('id', e.id);
    }
    if (toInsert.length > 0) {
      await supabase.from('employees').insert(toInsert.map(e => ({ org_id: orgId, ...fields(e) })));
    }
    if (toDelete.length > 0) {
      await supabase.from('employees').delete().in('id', toDelete);
    }

    await persistSection(5);
    setSaving(false);
    setSection(5);
  }

async function saveS5() {
    setSaving(true);
    await supabase.from('organizations').update({ scheduling_rules: rules }).eq('id', orgId);
    await persistSection(6);
    setSaving(false);
    setSection(6);
  }

 async function saveS6() {
    setSaving(true);
    await supabase.from('organizations').update({ access_settings: access }).eq('id', orgId);
    await persistSection(7);
    setSaving(false);
    setSection(7);
  }
  
async function saveS7() {
    setSaving(true);
    await supabase.from('organizations').update({ notification_settings: notifs }).eq('id', orgId);
    await persistSection(8);
    setSaving(false);
    setSection(8);
  }

async function saveS8() {
    setSaving(true);
    await supabase.from('organizations').update({ coverage_settings: coverage }).eq('id', orgId);
    await persistSection(9);
    setSaving(false);
    setSection(9);
  }

async function saveS9() {
    setSaving(true);
    setFinishError('');
    const { error } = await supabase
      .from('organizations')
      .update({ setup_completed_at: new Date().toISOString() })
      .eq('id', orgId);
    setSaving(false);
    if (error) {
      setFinishError('Could not finish setup. Please try again.');
      return;
    }
    if (onComplete) onComplete();
  }
  

  function addShift() {
    if (!newShift.startTime || !newShift.endTime) return;
    setWizardShifts(prev => [...prev, { id: Date.now(), day: activeDay, startTime: newShift.startTime, endTime: newShift.endTime, hours: newShift.hours, peopleNeeded: parseInt(newShift.peopleNeeded) || 1, certifications: newShift.certifications }]);
    setNewShift({ startTime: '', endTime: '', hours: '', peopleNeeded: 1, certifications: '' });
  }

  function addEmployee() {
    if (!newEmployee.name || !newEmployee.role || !newEmployee.email) { alert('Name, role, and email are required.'); return; }
    setWizardStaff(prev => [...prev, { ...newEmployee, id: Date.now() }]);
    setNewEmployee({ name: '', role: '', email: '', phone: '', max_hours: '', priority: 'Standard', available_days: '', preferred_shifts: '', certifications: '' });
    setShowMoreFields(false);
  }

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: COLORS.bg, minHeight: '100vh' }}>
      <div style={{ background: COLORS.navy, padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
        <div style={{ color: '#fff', fontWeight: 800, fontSize: 20, letterSpacing: '-0.03em' }}>THRIVE<span style={{ color: COLORS.greenBright }}>AI</span></div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>Organization Setup</div>
      </div>

      <ProgressBar section={section} />

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 24px' }}>

        {/* SECTION 1 */}
        {section === 1 && (
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.navy, margin: '0 0 8px', letterSpacing: '-0.03em' }}>Let's set up your organization</h1>
            <p style={{ color: COLORS.textBody, fontSize: 15, marginBottom: 32, lineHeight: 1.6 }}>This takes about 10 minutes. You can always come back and change these settings later.</p>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>What is the name of your organization? <span style={{ color: '#e53e3e' }}>*</span></label>
              <input style={{ ...inputStyle, borderColor: errors.orgName ? '#e53e3e' : COLORS.border }} placeholder="e.g. Acme Fitness, City Grill, Sunrise Medical" value={orgName} onChange={e => setOrgName(titleCase(e.target.value))} />
              {errors.orgName && <p style={{ ...hintStyle, color: '#e53e3e' }}>{errors.orgName}</p>}
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>What industry are you in? <span style={{ color: '#e53e3e' }}>*</span></label>
              <p style={hintStyle}>Select all that apply — many organizations operate across multiple industries.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 10 }}>
                {INDUSTRIES.map(ind => (
                  <button key={ind} onClick={() => setIndustry(prev => prev.includes(ind) ? prev.filter(i => i !== ind) : [...prev, ind])} style={{ padding: '12px 10px', borderRadius: 8, cursor: 'pointer', border: `2px solid ${industry.includes(ind) ? COLORS.greenBright : COLORS.border}`, background: industry.includes(ind) ? 'rgba(34,197,122,0.08)' : '#fff', color: industry.includes(ind) ? COLORS.navy : COLORS.textBody, fontSize: 13, fontWeight: industry.includes(ind) ? 700 : 400, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    {industry.includes(ind) && '✓ '}{ind}
                  </button>
                ))}
              </div>
              {industry.length > 0 && <p style={{ ...hintStyle, color: COLORS.green, marginTop: 8 }}>{industry.length} selected: {industry.join(', ')}</p>}
              {errors.industry && <p style={{ ...hintStyle, color: '#e53e3e', marginTop: 8 }}>{errors.industry}</p>}
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Where are you located? <span style={{ color: '#e53e3e' }}>*</span></label>
              <p style={hintStyle}>Enter your city name exactly as it appears officially.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginTop: 8 }}>
                <div>
                  <input style={{ ...inputStyle, borderColor: errors.city ? '#e53e3e' : COLORS.border }} placeholder="City" value={city} onChange={e => setCity(titleCase(e.target.value))} />
                  {errors.city && <p style={{ ...hintStyle, color: '#e53e3e' }}>{errors.city}</p>}
                </div>
                <div>
                  <select style={{ ...inputStyle, borderColor: errors.state ? '#e53e3e' : COLORS.border }} value={state} onChange={e => setState(e.target.value)}>
                    <option value="">State...</option>
                    {US_STATES.map(s => <option key={s.abbr} value={s.abbr}>{s.abbr} — {s.name}</option>)}
                  </select>
                  {errors.state && <p style={{ ...hintStyle, color: '#e53e3e' }}>{errors.state}</p>}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>What timezone are you in?</label>
              <select style={inputStyle} value={timezone} onChange={e => setTimezone(e.target.value)}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace('America/', '').replace('Pacific/', 'Pacific/').replace(/_/g, ' ')}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>How often do you create a new schedule?</label>
              <p style={hintStyle}>Most organizations schedule weekly, but choose what fits your operation.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 10 }}>
                {CADENCES.map(c => (
                  <button key={c.value} onClick={() => setCadence(c.value)} style={{ padding: '14px 10px', borderRadius: 8, cursor: 'pointer', textAlign: 'left', border: `2px solid ${cadence === c.value ? COLORS.greenBright : COLORS.border}`, background: cadence === c.value ? 'rgba(34,197,122,0.08)' : '#fff' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>{c.label}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{c.description}</div>
                  </button>
                ))}
              </div>
              {cadence === 'custom' && (
                <div style={{ marginTop: 12 }}>
                  <label style={{ ...labelStyle, fontSize: 12 }}>How many days in your cycle?</label>
                  <input style={{ ...inputStyle, width: 160, borderColor: errors.customDays ? '#e53e3e' : COLORS.border }} type="number" placeholder="e.g. 10" value={customDays} onChange={e => setCustomDays(e.target.value)} />
                  {errors.customDays && <p style={{ ...hintStyle, color: '#e53e3e' }}>{errors.customDays}</p>}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>How far in advance must you publish a schedule?</label>
              <p style={hintStyle}>Select "No requirement" if this doesn't apply to you.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 10 }}>
                {NOTICE_OPTIONS.map(n => (
                  <button key={n.value} onClick={() => setMinNotice(n.value)} style={{ padding: '14px 10px', borderRadius: 8, cursor: 'pointer', textAlign: 'left', border: `2px solid ${minNotice === n.value ? COLORS.greenBright : COLORS.border}`, background: minNotice === n.value ? 'rgba(34,197,122,0.08)' : '#fff' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>{n.label}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{n.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 40, background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>Track public holidays</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>The system will flag when shifts fall on public holidays</div>
              </div>
              <Toggle value={holidays} onChange={setHolidays} />
            </div>

            <NavButtons onNext={saveS1} nextLabel="Continue to Roles →" saving={saving} />
          </div>
        )}

        {/* SECTION 2 */}
        {section === 2 && (
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.navy, margin: '0 0 8px', letterSpacing: '-0.03em' }}>Define your roles</h1>
            <p style={{ color: COLORS.textBody, fontSize: 15, marginBottom: 8, lineHeight: 1.6 }}>Roles are the job types in your organization. Every employee will be assigned one of these roles.</p>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 32 }}>💡 Start with your most common roles. You can always add more later.</p>

            {industry.length > 0 && (
              <div style={{ marginBottom: 28 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginBottom: 10 }}>Quick add — common roles for {industry.join(' & ')}:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {getSuggestedRoles(industry).map(r => {
                    const added = wizardRoles.some(wr => wr.name.toLowerCase() === r.toLowerCase());
                    return (
                      <button key={r} onClick={() => { if (!added) setWizardRoles(prev => [...prev, { id: Date.now() + r, name: r }]); }} style={{ padding: '7px 14px', borderRadius: 100, cursor: 'pointer', border: `1px solid ${added ? COLORS.greenBright : COLORS.border}`, background: added ? 'rgba(34,197,122,0.1)' : '#fff', color: added ? COLORS.green : COLORS.textBody, fontSize: 13, fontWeight: 500 }}>
                        {added ? '✓ ' : '+ '}{r}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ background: COLORS.navy, padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Your Roles</span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{wizardRoles.length} added</span>
              </div>
              {wizardRoles.length === 0 ? (
                <div style={{ padding: '24px 20px', textAlign: 'center', color: COLORS.textMuted, fontSize: 14 }}>No roles added yet. Use quick add above or type a custom role below.</div>
              ) : (
                wizardRoles.map((role, i) => (
                  <div key={role.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: i < wizardRoles.length - 1 ? `1px solid ${COLORS.border}` : 'none' }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: COLORS.navy }}>{role.name}</span>
                    <button onClick={() => setWizardRoles(prev => prev.filter(r => r.id !== role.id))} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                  </div>
                ))
              )}
              <div style={{ padding: '12px 20px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: 10 }}>
                <input style={{ ...inputStyle, flex: 1 }} placeholder="Type a custom role name..." value={newRoleName} onChange={e => setNewRoleName(titleCase(e.target.value))}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newRoleName.trim()) {
                      if (!wizardRoles.some(r => r.name.toLowerCase() === newRoleName.trim().toLowerCase())) setWizardRoles(prev => [...prev, { id: Date.now(), name: newRoleName.trim() }]);
                      setNewRoleName('');
                    }
                  }}
                />
                <button onClick={() => {
                  if (newRoleName.trim() && !wizardRoles.some(r => r.name.toLowerCase() === newRoleName.trim().toLowerCase())) { setWizardRoles(prev => [...prev, { id: Date.now(), name: newRoleName.trim() }]); setNewRoleName(''); }
                }} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: COLORS.greenBright, color: COLORS.navy, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Add Role</button>
              </div>
            </div>

            {wizardRoles.length === 0 && <p style={{ color: '#e53e3e', fontSize: 13, marginBottom: 16 }}>Please add at least one role before continuing.</p>}
            <NavButtons onBack={() => setSection(1)} onNext={saveS2} nextLabel="Continue to Shifts →" saving={saving} disabled={wizardRoles.length === 0} />
          </div>
        )}

        {/* SECTION 3 */}
        {section === 3 && (
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.navy, margin: '0 0 8px', letterSpacing: '-0.03em' }}>Define your shifts</h1>
            <p style={{ color: COLORS.textBody, fontSize: 15, marginBottom: 8, lineHeight: 1.6 }}>Shifts are the time slots your organization needs covered each day. You can always adjust these later.</p>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 24 }}>💡 Add your typical week. If weekends look different, add those separately.</p>

            <ModeToggle value={shiftMode} onChange={setShiftMode} options={[
              { value: 'manual', icon: '✏️', label: 'Define Manually', desc: 'Add shifts day by day' },
              { value: 'csv', icon: '📄', label: 'Upload Spreadsheet', desc: 'Import an existing schedule' },
            ]} />

            {shiftMode === 'manual' && (
              <div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' }}>
                  {DAYS.map(day => {
                    const count = wizardShifts.filter(s => s.day === day).length;
                    return (
                      <button key={day} onClick={() => setActiveDay(day)} style={{ padding: '8px 16px', borderRadius: 8, cursor: 'pointer', position: 'relative', border: `2px solid ${activeDay === day ? COLORS.greenBright : COLORS.border}`, background: activeDay === day ? 'rgba(34,197,122,0.08)' : '#fff', color: activeDay === day ? COLORS.navy : COLORS.textBody, fontSize: 13, fontWeight: activeDay === day ? 700 : 400 }}>
                        {day.slice(0, 3)}
                        {count > 0 && <span style={{ position: 'absolute', top: -6, right: -6, background: COLORS.greenBright, color: COLORS.navy, borderRadius: '50%', width: 16, height: 16, fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{count}</span>}
                      </button>
                    );
                  })}
                </div>

                <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
                  <div style={{ background: COLORS.navy, padding: '12px 20px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{activeDay} Shifts</span>
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{wizardShifts.filter(s => s.day === activeDay).length} shifts</span>
                  </div>
                  {wizardShifts.filter(s => s.day === activeDay).length === 0 ? (
                    <div style={{ padding: '24px 20px', textAlign: 'center', color: COLORS.textMuted, fontSize: 14 }}>No shifts for {activeDay} yet.</div>
                  ) : (
                    wizardShifts.filter(s => s.day === activeDay).map(shift => (
                      <div key={shift.id} style={{ padding: '14px 20px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.navy }}>{formatTime(shift.startTime)} – {formatTime(shift.endTime)}</div>
                          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{shift.hours}h · {shift.peopleNeeded} {shift.peopleNeeded === 1 ? 'person' : 'people'} needed{shift.certifications && ` · ${shift.certifications}`}</div>
                        </div>
                        <button onClick={() => setWizardShifts(prev => prev.filter(s => s.id !== shift.id))} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                      </div>
                    ))
                  )}
                  <div style={{ padding: '16px 20px', borderTop: `1px solid ${COLORS.border}`, background: '#fafbfc' }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: COLORS.navy, marginBottom: 12 }}>Add a shift for {activeDay}:</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={{ ...labelStyle, fontSize: 11 }}>Start Time</label>
                        <input type="time" style={inputStyle} value={newShift.startTime} onChange={e => setNewShift(s => ({ ...s, startTime: e.target.value, hours: calcHours(e.target.value, s.endTime) }))} />
                      </div>
                      <div>
                        <label style={{ ...labelStyle, fontSize: 11 }}>End Time</label>
                        <input type="time" style={inputStyle} value={newShift.endTime} onChange={e => setNewShift(s => ({ ...s, endTime: e.target.value, hours: calcHours(s.startTime, e.target.value) }))} />
                      </div>
                      <div>
                        <label style={{ ...labelStyle, fontSize: 11 }}>Hours</label>
                        <input style={{ ...inputStyle, background: '#f0f0f0', color: COLORS.textMuted }} value={newShift.hours ? `${newShift.hours}h` : 'Auto'} readOnly />
                      </div>
                      <div>
                        <label style={{ ...labelStyle, fontSize: 11 }}>People Needed</label>
                        <input type="number" min="1" style={inputStyle} value={newShift.peopleNeeded} onChange={e => setNewShift(s => ({ ...s, peopleNeeded: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ ...labelStyle, fontSize: 11 }}>Certifications Required <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>(optional)</span></label>
                      <input style={inputStyle} placeholder="e.g. CPR, Lifeguard Certified" value={newShift.certifications} onChange={e => setNewShift(s => ({ ...s, certifications: e.target.value }))} />
                    </div>
                    <button onClick={addShift} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: COLORS.greenBright, color: COLORS.navy, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ Add This Shift</button>
                  </div>
                </div>
              </div>
            )}

            {shiftMode === 'csv' && (
              <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '24px', marginBottom: 16 }}>
                <p style={{ fontSize: 15, fontWeight: 700, color: COLORS.navy, marginBottom: 8 }}>Upload your shift schedule</p>
                <p style={{ fontSize: 13, color: COLORS.textBody, marginBottom: 16, lineHeight: 1.6 }}>Copy the template into Google Sheets, Excel, or Numbers. Fill in your shifts, export as CSV, upload here.</p>
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>⚠️ Follow these formatting rules exactly:</p>
                  <ul style={{ fontSize: 12, color: '#92400e', paddingLeft: 16, margin: 0, lineHeight: 1.8 }}>
                    <li><strong>Day</strong> — Full name: <code>Monday</code> not <code>Mon</code></li>
                    <li><strong>Times</strong> — 12-hour format: <code>9:00 AM</code> not <code>9am</code></li>
                    <li><strong>People Needed</strong> — Number only: <code>2</code></li>
                    <li><strong>Certifications</strong> — Comma separated or leave blank</li>
                  </ul>
                </div>
                <button onClick={() => { const text = `Day\tStart Time\tEnd Time\tPeople Needed\tCertifications Required\nMonday\t9:00 AM\t1:00 PM\t2\tCPR\nMonday\t1:00 PM\t5:00 PM\t1\t\nTuesday\t9:00 AM\t5:00 PM\t3\tLifeguard`; navigator.clipboard.writeText(text).then(() => alert('✅ Template copied! Paste into your spreadsheet, fill in your shifts, export as CSV, then upload below.')); }} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: COLORS.navy, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  📋 Copy Template to Clipboard
                </button>
                <div style={{ border: `2px dashed ${COLORS.border}`, borderRadius: 10, padding: '24px', textAlign: 'center' }}>
                  <p style={{ fontSize: 14, color: COLORS.textBody, marginBottom: 12 }}>Upload your completed CSV:</p>
                  <input type="file" accept=".csv" style={{ fontSize: 13 }} onChange={e => {
                    const file = e.target.files[0]; if (!file) return;
                    const reader = new FileReader();
                    reader.onload = evt => {
                      const lines = evt.target.result.split('\n').filter(l => l.trim());
                      const validDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                      const errs = [];
                      const rows = lines.slice(1).map((line, i) => {
                        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
                        const [day, startTime, endTime, peopleNeeded, certifications] = cols;
                        if (!validDays.includes(day)) errs.push(`Row ${i + 2}: "${day}" is not a valid day name.`);
                        if (!/^\d{1,2}:\d{2} (AM|PM)$/.test(startTime)) errs.push(`Row ${i + 2}: Start time must be like 9:00 AM`);
                        if (!/^\d{1,2}:\d{2} (AM|PM)$/.test(endTime)) errs.push(`Row ${i + 2}: End time must be like 1:00 PM`);
                        return { id: Date.now() + i, day, startTime: to24(startTime), endTime: to24(endTime), hours: calcHours(to24(startTime), to24(endTime)), peopleNeeded: parseInt(peopleNeeded) || 1, certifications: certifications || '' };
                      }).filter(r => validDays.includes(r.day));
                      if (errs.length) { alert(`Fix these errors:\n\n${errs.join('\n')}`); return; }
                      setWizardShifts(prev => [...prev, ...rows]);
                      alert(`✅ ${rows.length} shifts imported!`);
                    };
                    reader.readAsText(file);
                  }} />
                </div>
              </div>
            )}

            {wizardShifts.length > 0 && (
              <div style={{ background: 'rgba(34,197,122,0.06)', border: '1px solid rgba(34,197,122,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.green, margin: 0 }}>✓ {wizardShifts.length} total shifts across {[...new Set(wizardShifts.map(s => s.day))].length} days</p>
              </div>
            )}

            <NavButtons onBack={() => setSection(2)} onNext={saveS3} nextLabel="Continue to Staff →" saving={saving} />
          </div>
        )}

        {/* SECTION 4 */}
        {section === 4 && (
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.navy, margin: '0 0 8px', letterSpacing: '-0.03em' }}>Add your staff</h1>
            <p style={{ color: COLORS.textBody, fontSize: 15, marginBottom: 8, lineHeight: 1.6 }}>Add your employees one at a time or upload a spreadsheet. Each employee will automatically receive an invite email to set up their account and fill out their availability.</p>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 24 }}>💡 Name, role, and email are required. The more info you add now, the better your first schedule will be.</p>

            <ModeToggle value={staffMode} onChange={setStaffMode} options={[
              { value: 'manual', icon: '✏️', label: 'Add Manually', desc: 'Add employees one at a time' },
              { value: 'csv', icon: '📄', label: 'Upload Spreadsheet', desc: 'Import multiple employees at once' },
            ]} />

            {staffMode === 'manual' && (
              <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ background: COLORS.navy, padding: '12px 20px' }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Add an Employee</span>
                </div>
                <div style={{ padding: '20px' }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: COLORS.greenBright, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Required</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
                    <div>
                      <label style={labelStyle}>Full Name <span style={{ color: '#e53e3e' }}>*</span></label>
                      <input style={inputStyle} placeholder="e.g. Jordan Smith" value={newEmployee.name} onChange={e => setNewEmployee(s => ({ ...s, name: titleCase(e.target.value) }))} />
                    </div>
                    <div>
                      <label style={labelStyle}>Role <span style={{ color: '#e53e3e' }}>*</span></label>
                      <select style={inputStyle} value={newEmployee.role} onChange={e => setNewEmployee(s => ({ ...s, role: e.target.value }))}>
                        <option value="">Select role...</option>
                        {wizardRoles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Email <span style={{ color: '#e53e3e' }}>*</span></label>
                      <input style={inputStyle} type="email" placeholder="jordan@company.com" value={newEmployee.email} onChange={e => setNewEmployee(s => ({ ...s, email: e.target.value }))} />
                    </div>
                  </div>
                  <button onClick={() => setShowMoreFields(v => !v)} style={{ background: 'none', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, color: COLORS.textBody, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{showMoreFields ? '▾' : '▸'}</span>
                    {showMoreFields ? 'Hide' : 'Add more info'} — phone, hours, availability
                    <span style={{ fontSize: 11, color: COLORS.green, fontWeight: 700 }}>Recommended</span>
                  </button>
                  {showMoreFields && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                        <div>
                          <label style={labelStyle}>Phone</label>
                          <input style={inputStyle} placeholder="802-555-0100" value={newEmployee.phone} onChange={e => setNewEmployee(s => ({ ...s, phone: e.target.value }))} />
                        </div>
                        <div>
                          <label style={labelStyle}>Max Hours / Week</label>
                          <input style={inputStyle} type="number" placeholder="e.g. 40" value={newEmployee.max_hours} onChange={e => setNewEmployee(s => ({ ...s, max_hours: e.target.value }))} />
                        </div>
                        <div>
                          <label style={labelStyle}>Priority</label>
                          <select style={inputStyle} value={newEmployee.priority} onChange={e => setNewEmployee(s => ({ ...s, priority: e.target.value }))}>
                            <option value="High">High</option>
                            <option value="Standard">Standard</option>
                            <option value="Low">Low</option>
                          </select>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                        <div>
                          <label style={labelStyle}>Available Days</label>
                          <input style={inputStyle} placeholder="e.g. Mon, Tue, Wed" value={newEmployee.available_days} onChange={e => setNewEmployee(s => ({ ...s, available_days: e.target.value }))} />
                        </div>
                        <div>
                          <label style={labelStyle}>Preferred Shifts</label>
                          <input style={inputStyle} placeholder="e.g. Morning, Evening" value={newEmployee.preferred_shifts} onChange={e => setNewEmployee(s => ({ ...s, preferred_shifts: e.target.value }))} />
                        </div>
                      </div>
                      <div>
                        <label style={labelStyle}>Certifications</label>
                        <input style={inputStyle} placeholder="e.g. CPR, Lifeguard Certified" value={newEmployee.certifications} onChange={e => setNewEmployee(s => ({ ...s, certifications: e.target.value }))} />
                      </div>
                    </div>
                  )}
                  <button onClick={addEmployee} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: COLORS.greenBright, color: COLORS.navy, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ Add Employee & Send Invite</button>
                </div>
              </div>
            )}

            {staffMode === 'csv' && (
              <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '24px', marginBottom: 16 }}>
                <p style={{ fontSize: 15, fontWeight: 700, color: COLORS.navy, marginBottom: 8 }}>Upload your staff spreadsheet</p>
                <p style={{ fontSize: 13, color: COLORS.textBody, marginBottom: 16, lineHeight: 1.6 }}>Copy the template into Google Sheets, Excel, or Numbers. Fill in your staff, export as CSV, upload here.</p>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                  {['Google Sheets', 'Microsoft Excel', 'Apple Numbers', 'LibreOffice'].map(app => (
                    <span key={app} style={{ padding: '5px 12px', borderRadius: 100, border: `1px solid ${COLORS.border}`, fontSize: 12, fontWeight: 600, color: COLORS.textBody, background: '#f9fafb' }}>{app}</span>
                  ))}
                </div>
                <button onClick={() => { const text = `Name\tRole\tEmail\tPhone\tMax Hours/Week\tAvailable Days\tPreferred Shifts\tCertifications\nJordan Smith\tManager\tjordan@org.com\t802-555-0100\t40\tMon, Tue, Wed\tMorning\tCPR`; navigator.clipboard.writeText(text).then(() => alert('✅ Template copied! Paste into your spreadsheet, fill in your staff, export as CSV, then upload below.')); }} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: COLORS.navy, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  📋 Copy Template to Clipboard
                </button>
                <div style={{ border: `2px dashed ${COLORS.border}`, borderRadius: 10, padding: '24px', textAlign: 'center' }}>
                  <p style={{ fontSize: 14, color: COLORS.textBody, marginBottom: 12 }}>Upload your completed CSV:</p>
                  <input type="file" accept=".csv" style={{ fontSize: 13 }} onChange={e => {
                    const file = e.target.files[0]; if (!file) return;
                    const reader = new FileReader();
                    reader.onload = evt => {
                      const lines = evt.target.result.split('\n').filter(l => l.trim());
                      const rows = lines.slice(1).map(line => {
                        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
                        return { id: Date.now() + Math.random(), name: cols[0] || '', role: cols[1] || '', email: cols[2] || '', phone: cols[3] || '', max_hours: cols[4] || '', available_days: cols[5] || '', preferred_shifts: cols[6] || '', certifications: cols[7] || '', priority: 'Standard' };
                      }).filter(r => r.name && r.email);
                      setWizardStaff(prev => [...prev, ...rows]);
                      alert(`✅ ${rows.length} employees imported!`);
                    };
                    reader.readAsText(file);
                  }} />
                </div>
              </div>
            )}

            {wizardStaff.length > 0 && (
              <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ background: COLORS.navy, padding: '12px 20px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Staff Added</span>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{wizardStaff.length} employees</span>
                </div>
                {wizardStaff.map((emp, i) => {
                  const filled = [emp.name, emp.role, emp.email, emp.phone, emp.max_hours, emp.available_days].filter(Boolean).length;
                  const pct = Math.round((filled / 6) * 100);
                  return (
                    <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px', borderBottom: i < wizardStaff.length - 1 ? `1px solid ${COLORS.border}` : 'none' }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.green})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>{emp.name.charAt(0)}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.navy }}>{emp.name}</div>
                        <div style={{ fontSize: 12, color: COLORS.textMuted }}>{emp.role} · {emp.email}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 11, color: pct === 100 ? COLORS.green : COLORS.textMuted, fontWeight: 600 }}>Profile {pct}%</div>
                      </div>
                      <button onClick={() => setWizardStaff(prev => prev.filter(e => e.id !== emp.id))} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                    </div>
                  );
                })}
              </div>
            )}

            <NavButtons onBack={() => setSection(3)} onNext={saveS4} nextLabel={wizardStaff.length === 0 ? 'Skip for Now →' : 'Continue to Rules →'} saving={saving} />
          </div>
        )}

        {/* SECTION 5 */}
        {section === 5 && (
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.navy, margin: '0 0 8px', letterSpacing: '-0.03em' }}>Scheduling rules</h1>
            <p style={{ color: COLORS.textBody, fontSize: 15, marginBottom: 8, lineHeight: 1.6 }}>These rules guide how Thrive AI builds your schedule. Every setting has a sensible default — adjust anything that doesn't fit your org.</p>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 32 }}>💡 You can change any of these rules at any time after setup.</p>

            <RuleCard title="Organization Hour Budgets" description="Set the total hours your org can schedule per period. Useful for budget planning.">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Max Hours / Week</label>
                  <input style={inputStyle} type="number" placeholder="e.g. 200" value={rules.orgMaxHoursWeek} onChange={e => setRules(r => ({ ...r, orgMaxHoursWeek: e.target.value }))} />
                  <p style={hintStyle}>Leave blank for no limit</p>
                </div>
                <div>
                  <label style={labelStyle}>Max Hours / Month</label>
                  <input style={inputStyle} type="number" placeholder="e.g. 800" value={rules.orgMaxHoursMonth} onChange={e => setRules(r => ({ ...r, orgMaxHoursMonth: e.target.value }))} />
                  <p style={hintStyle}>Leave blank for no limit</p>
                </div>
                <div>
                  <label style={labelStyle}>Max Hours / Period</label>
                  <input style={inputStyle} type="number" placeholder="e.g. 2400" value={rules.orgMaxHoursPeriod} onChange={e => setRules(r => ({ ...r, orgMaxHoursPeriod: e.target.value }))} />
                  <p style={hintStyle}>Semester, quarter, etc.</p>
                </div>
              </div>
              <ToggleRow label="Block publishing when budget is exceeded" description="If off, manager gets a warning but can still publish." value={rules.blockOnBudgetExceeded} onChange={v => setRules(r => ({ ...r, blockOnBudgetExceeded: v }))} />
              <div style={{ marginTop: 14 }}>
                <label style={labelStyle}>Warn manager when budget reaches</label>
                <PctPicker value={rules.budgetWarnPct} onChange={v => setRules(r => ({ ...r, budgetWarnPct: v }))} />
              </div>
            </RuleCard>

            <RuleCard title="Staff Scheduling" description="Controls how individual employees are scheduled.">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 4 }}>
                <div>
                  <label style={labelStyle}>Minimum shift length (hours)</label>
                  <input style={inputStyle} type="number" min="0.5" step="0.5" placeholder="e.g. 2" value={rules.minShiftHours} onChange={e => setRules(r => ({ ...r, minShiftHours: e.target.value }))} />
                  <p style={hintStyle}>Default: 2 hours</p>
                </div>
                <div>
                  <label style={labelStyle}>Warn when employee reaches</label>
                  <PctPicker value={rules.empWarnPct} onChange={v => setRules(r => ({ ...r, empWarnPct: v }))} />
                  <p style={hintStyle}>of their max hours</p>
                </div>
              </div>
              <ToggleRow label="Enforce certification matching" description="Only assign employees to shifts where they have the required certifications." value={rules.certMatching} onChange={v => setRules(r => ({ ...r, certMatching: v }))} />
              <ToggleRow label="Respect priority order" description="High priority employees are scheduled before Standard, then Low." value={rules.respectPriority} onChange={v => setRules(r => ({ ...r, respectPriority: v }))} />
            </RuleCard>

            <RuleCard title="Clopen Detection" description="A 'clopen' is when an employee closes one night and opens the next morning — hard on morale.">
              <ToggleRow label="Enable clopen detection" description="Flag when an employee is scheduled to close and open back-to-back." value={rules.clopenDetection} onChange={v => setRules(r => ({ ...r, clopenDetection: v }))} />
              {rules.clopenDetection && (
                <div style={{ marginTop: 14 }}>
                  <label style={labelStyle}>Minimum rest period between shifts</label>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, marginBottom: 4 }}>
                    {[6, 8, 10, 12].map(hrs => (
                      <button key={hrs} onClick={() => setRules(r => ({ ...r, minRestHours: hrs }))} style={{ padding: '7px 18px', borderRadius: 8, cursor: 'pointer', border: `2px solid ${rules.minRestHours === hrs ? COLORS.greenBright : COLORS.border}`, background: rules.minRestHours === hrs ? 'rgba(34,197,122,0.08)' : '#fff', fontSize: 13, fontWeight: rules.minRestHours === hrs ? 700 : 400 }}>{hrs}h</button>
                    ))}
                  </div>
                  <p style={hintStyle}>Default: 8 hours. Industry standard for most organizations.</p>
                  <ToggleRow label="Hard block clopens" description="If on, prevents scheduling clopens entirely. If off, flags them and lets the manager override." value={rules.clopenHardBlock} onChange={v => setRules(r => ({ ...r, clopenHardBlock: v }))} />
                </div>
              )}
            </RuleCard>

            <NavButtons onBack={() => setSection(4)} onNext={saveS5} nextLabel="Continue to Access →" saving={saving} />
          </div>
        )}

        {/* SECTION 6 */}
        {section === 6 && (
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.navy, margin: '0 0 8px', letterSpacing: '-0.03em' }}>Access & permissions</h1>
            <p style={{ color: COLORS.textBody, fontSize: 15, marginBottom: 8, lineHeight: 1.6 }}>Decide who can do what. You can always grant or restrict permissions per-person later from the Staff page.</p>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 32 }}>💡 You're the Owner — you have full access to everything. These settings control what other managers and employees can do.</p>

            <RuleCard title="Manager Access" description="Controls what managers you invite can do.">
              <div style={{ background: 'rgba(34,197,122,0.08)', border: `1px solid ${COLORS.greenBright}`, borderRadius: 8, padding: '12px 16px', marginBottom: 18 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: COLORS.green, margin: 0, lineHeight: 1.5 }}>👑 You're the Owner. You have full access to everything by default. Invite teammates from the Staff page later to grant them manager permissions.</p>
              </div>
              <ToggleRow label="Allow managers to invite other managers" description="If off, only the Owner can invite new managers." value={access.managersCanInviteManagers} onChange={v => setAccess(a => ({ ...a, managersCanInviteManagers: v }))} />
              <ToggleRow label="Require Owner approval to publish schedules" description="If on, managers can build schedules but Owner must approve before they go live." value={access.requireOwnerApprovalToPublish} onChange={v => setAccess(a => ({ ...a, requireOwnerApprovalToPublish: v }))} />
              <ToggleRow label="Managers can edit org-wide rules" description="Includes scheduling rules, hour budgets, and clopen settings (Section 5)." value={access.managersCanEditRules} onChange={v => setAccess(a => ({ ...a, managersCanEditRules: v }))} />
              <div style={{ marginTop: 22, padding: '16px 18px', background: '#fafbfc', border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginBottom: 4 }}>Default permissions for new managers</p>
                <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 12 }}>When you invite a new manager, they'll start with these permissions. You can customize per-person later.</p>
                {[
                  { key: 'editSchedules', label: 'Edit & publish schedules' },
                  { key: 'approveTimeOff', label: 'Approve time-off requests' },
                  { key: 'approveSwaps', label: 'Approve shift swaps' },
                  { key: 'manageEmployees', label: 'Add & remove employees' },
                  { key: 'editOrgRules', label: 'Edit org-wide rules' },
                  { key: 'viewLaborCosts', label: 'View labor cost reports' },
                ].map(perm => (
                  <div key={perm.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
                    <button onClick={() => setAccess(a => ({ ...a, managerDefaults: { ...(a.managerDefaults || {}), [perm.key]: !(a.managerDefaults || {})[perm.key] } }))} style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${(access.managerDefaults || {})[perm.key] ? COLORS.greenBright : COLORS.border}`, background: (access.managerDefaults || {})[perm.key] ? COLORS.greenBright : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {(access.managerDefaults || {})[perm.key] && <span style={{ color: '#fff', fontSize: 14, fontWeight: 800, lineHeight: 1 }}>✓</span>}
                    </button>
                    <span style={{ fontSize: 13, color: COLORS.navy, fontWeight: 500 }}>{perm.label}</span>
                  </div>
                ))}
              </div>
            </RuleCard>

            <RuleCard title="Employee Access" description="Controls what your employees can do from their own logins.">
              <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginBottom: 4 }}>Employees can:</p>
              <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 4 }}>Toggle off anything you don't want employees to access.</p>
              <ToggleRow label="View their own schedule" description="See shifts assigned to them." value={access.empViewOwnSchedule} onChange={v => setAccess(a => ({ ...a, empViewOwnSchedule: v }))} />
              <ToggleRow label="Submit availability" description="Tell you when they can and can't work." value={access.empSubmitAvailability} onChange={v => setAccess(a => ({ ...a, empSubmitAvailability: v }))} />
              <ToggleRow label="Request time off" description="Submit time-off requests through Thrive." value={access.empRequestTimeOff} onChange={v => setAccess(a => ({ ...a, empRequestTimeOff: v }))} />
              <ToggleRow label="Request shift swaps" description="Trade shifts with a coworker." value={access.empRequestSwaps} onChange={v => setAccess(a => ({ ...a, empRequestSwaps: v }))} />
              <ToggleRow label="Claim open shifts" description="Pick up unassigned or callout shifts." value={access.empClaimOpenShifts} onChange={v => setAccess(a => ({ ...a, empClaimOpenShifts: v }))} />
              <ToggleRow label="View coworkers' schedules" description="See who else is working when." value={access.empViewCoworkerSchedules} onChange={v => setAccess(a => ({ ...a, empViewCoworkerSchedules: v }))} />
              <ToggleRow label="Update their own contact info & certifications" description="Edit phone, email, and upload cert renewals." value={access.empUpdateOwnInfo} onChange={v => setAccess(a => ({ ...a, empUpdateOwnInfo: v }))} />
            </RuleCard>

            <RuleCard title="Approval Workflows" description="How requests flow through your org.">
              <div style={{ marginBottom: 22 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginBottom: 8 }}>Shift swaps</p>
                <div style={{ background: 'rgba(34,197,122,0.08)', border: `1px solid ${COLORS.greenBright}`, borderRadius: 8, padding: '12px 14px' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: COLORS.navy, margin: '0 0 4px' }}>✓ Auto-approved when both employees agree</p>
                  <p style={{ fontSize: 12, color: COLORS.textBody, margin: 0, lineHeight: 1.5 }}>The manager will receive a notification each time a swap goes through, but doesn't need to approve it.</p>
                </div>
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginBottom: 4 }}>Time-off requests</p>
                <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 10 }}>Choose how time-off flows at your org.</p>
                {[
                  { value: 'auto_no_gap', label: 'Auto-approve if no coverage gap', desc: 'Most automated — system approves if shifts can still be filled.' },
                  { value: 'manager_approve_all', label: 'Manager must approve every request', desc: 'Most control — every request is reviewed by a manager.' },
                  { value: 'manager_approve_with_pto', label: 'Manager approves; employees see PTO balance', desc: 'Same as above, but employees can see their accrued time.' },
                  { value: 'external', label: "Don't handle through Thrive", desc: 'Time-off is tracked in another system (HRIS, paper, etc.)' },
                ].map(opt => (
                  <button key={opt.value} onClick={() => setAccess(a => ({ ...a, timeOffModel: opt.value }))} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 8, marginBottom: 8, cursor: 'pointer', border: `2px solid ${access.timeOffModel === opt.value ? COLORS.greenBright : COLORS.border}`, background: access.timeOffModel === opt.value ? 'rgba(34,197,122,0.08)' : '#fff' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>{opt.label}</div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
            </RuleCard>

            <NavButtons onBack={() => setSection(5)} onNext={saveS6} nextLabel="Continue to Notifications →" saving={saving} />
          </div>
        )}

        {/* SECTION 7 */}
        {section === 7 && (
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.navy, margin: '0 0 8px', letterSpacing: '-0.03em' }}>Notifications</h1>
            <p style={{ color: COLORS.textBody, fontSize: 15, marginBottom: 8, lineHeight: 1.6 }}>Set how your team gets notified when something happens. Email is on by default — employees can add SMS for any event from their account.</p>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 32 }}>💡 Email is reliable and works everywhere. SMS is opt-in and uses message credits.</p>
          
            <RuleCard title="Default Channels" description="Which notification channels are turned on across the org by default.">
              <ToggleRow label="Email" description="Reliable and works for everyone. The default channel at launch." value={(notifs.channelDefaults || {}).email} onChange={v => setNotifs(n => ({ ...n, channelDefaults: { ...(n.channelDefaults || {}), email: v } }))} />
              <ToggleRow label="SMS / Text message" description="Most attention-grabbing, but uses message credits. Off by default." value={(notifs.channelDefaults || {}).sms} onChange={v => setNotifs(n => ({ ...n, channelDefaults: { ...(n.channelDefaults || {}), sms: v } }))} />
            </RuleCard>

            <RuleCard title="Notification Events" description="Which events the system is allowed to send notifications for.">
              <p style={{ fontSize: 11, fontWeight: 700, color: COLORS.greenBright, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Employee events</p>
              <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 4 }}>Notifications sent to employees.</p>
              <ToggleRow label="New schedule published" description="When a new schedule goes live." value={notifs.events.schedulePublished} onChange={v => setNotifs(n => ({ ...n, events: { ...n.events, schedulePublished: v } }))} />
              <ToggleRow label="Shift assigned or changed" description="When an employee gets a new shift or one of theirs is edited." value={notifs.events.shiftAssignedOrChanged} onChange={v => setNotifs(n => ({ ...n, events: { ...n.events, shiftAssignedOrChanged: v } }))} />
              <ToggleRow label="Shift reminder" description="A heads-up before a shift starts (timing configurable later)." value={notifs.events.shiftReminder} onChange={v => setNotifs(n => ({ ...n, events: { ...n.events, shiftReminder: v } }))} />
              <ToggleRow label="Shift swap request received" description="When a coworker asks to trade shifts." value={notifs.events.swapRequestReceived} onChange={v => setNotifs(n => ({ ...n, events: { ...n.events, swapRequestReceived: v } }))} />
              <ToggleRow label="Time-off request status update" description="When a time-off request is approved or denied." value={notifs.events.timeOffStatusUpdate} onChange={v => setNotifs(n => ({ ...n, events: { ...n.events, timeOffStatusUpdate: v } }))} />
              <ToggleRow label="Open shift available to claim" description="When an unfilled shift is available." value={notifs.events.openShiftAvailable} onChange={v => setNotifs(n => ({ ...n, events: { ...n.events, openShiftAvailable: v } }))} />

              <p style={{ fontSize: 11, fontWeight: 700, color: COLORS.greenBright, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 22, marginBottom: 4 }}>Manager events</p>
              <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 4 }}>Notifications sent to managers.</p>
              <ToggleRow label="Time-off request submitted" description="When an employee requests time off." value={notifs.events.mgrTimeOffSubmitted} onChange={v => setNotifs(n => ({ ...n, events: { ...n.events, mgrTimeOffSubmitted: v } }))} />
              <ToggleRow label="Schedule needs review" description="When a new schedule is built and ready for approval." value={notifs.events.mgrScheduleNeedsReview} onChange={v => setNotifs(n => ({ ...n, events: { ...n.events, mgrScheduleNeedsReview: v } }))} />
              <ToggleRow label="Budget warning hit" description="When labor hours approach the configured budget threshold." value={notifs.events.mgrBudgetWarning} onChange={v => setNotifs(n => ({ ...n, events: { ...n.events, mgrBudgetWarning: v } }))} />
            </RuleCard>

            <RuleCard title="Employee Customization" description="Whether employees can adjust their own notification preferences.">
              <ToggleRow label="Allow employees to customize their own preferences" description="If on, each employee can pick their own channels and toggle individual events." value={notifs.allowEmployeeCustomization} onChange={v => setNotifs(n => ({ ...n, allowEmployeeCustomization: v }))} />

              {notifs.allowEmployeeCustomization && (
                <div style={{ marginTop: 16, padding: '14px 16px', background: 'rgba(34,197,122,0.08)', border: `1px solid ${COLORS.greenBright}`, borderRadius: 8 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.green, margin: '0 0 4px' }}>✓ Employees can override these defaults</p>
                  <p style={{ fontSize: 12, color: COLORS.textBody, margin: 0, lineHeight: 1.5 }}>Each employee will see a notifications page in their account where they can pick channels and turn individual events on or off.</p>
                </div>
              )}

              
            </RuleCard>

            <NavButtons onBack={() => setSection(6)} onNext={saveS7} nextLabel="Continue to Coverage →" saving={saving} />
          </div>
        )}

        {/* SECTION 8 */}
        {section === 8 && (
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.navy, margin: '0 0 8px', letterSpacing: '-0.03em' }}>Coverage</h1>
            <p style={{ color: COLORS.textBody, fontSize: 15, marginBottom: 8, lineHeight: 1.6 }}>How Thrive helps your team spot and fill coverage gaps. All settings are optional and can be changed later.</p>
            <div style={{ background: 'rgba(34,197,122,0.08)', border: `1px solid ${COLORS.greenBright}`, borderRadius: 8, padding: '12px 16px', marginBottom: 32 }}>
              <p style={{ fontSize: 12, color: COLORS.green, margin: 0, lineHeight: 1.5 }}>💡 Thrive helps you spot coverage issues, but you know your operation, customers, and any legal requirements better than we do. These are reminders for your team — not compliance rules.</p>
            </div>

            <RuleCard title="Coverage Gap Behavior" description="What happens when a shift can't be filled.">
              <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 10 }}>Choose how Thrive handles unfilled shifts. Thrive will never auto-assign an employee to a shift.</p>
              {[
                { value: 'open_shift', label: 'Auto-post as an open shift', desc: 'Eligible employees get notified and can claim the shift.' },
                { value: 'alert_manager', label: 'Alert the manager immediately', desc: 'A notification goes to the manager so they can solve it manually.' },
                { value: 'leave_open', label: 'Leave it open and flag the schedule', desc: 'No automatic action. The gap is visible on the schedule.' },
                { value: 'configurable', label: 'Configurable per shift', desc: 'Manager picks a default here, but can override the behavior on individual shifts.' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setCoverage(c => ({ ...c, gapMode: opt.value }))} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 8, marginBottom: 8, cursor: 'pointer', border: `2px solid ${coverage.gapMode === opt.value ? COLORS.greenBright : COLORS.border}`, background: coverage.gapMode === opt.value ? 'rgba(34,197,122,0.08)' : '#fff' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>{opt.label}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{opt.desc}</div>
                </button>
              ))}
            </RuleCard>

            <RuleCard title="Coverage Reminders" description="Soft reminders your org defines. Thrive will flag — your team decides.">
              <div style={{ marginBottom: 22 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginBottom: 4 }}>Always-required roles</p>
                <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 10 }}>Pick any roles that should always be on shift when you're open. Thrive will flag if they're missing.</p>
                {wizardRoles.length === 0 ? (
                  <div style={{ padding: '12px 14px', background: '#fafbfc', border: `1px dashed ${COLORS.border}`, borderRadius: 8, fontSize: 12, color: COLORS.textMuted }}>
                    Add roles in Section 2 first. You can come back here once you've defined your roles.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {wizardRoles.map(role => {
                      const selected = coverage.requiredRoles.includes(role.name);
                      return (
                        <button key={role.id} onClick={() => setCoverage(c => ({ ...c, requiredRoles: selected ? c.requiredRoles.filter(r => r !== role.name) : [...c.requiredRoles, role.name] }))} style={{ padding: '7px 14px', borderRadius: 100, cursor: 'pointer', border: `1px solid ${selected ? COLORS.greenBright : COLORS.border}`, background: selected ? 'rgba(34,197,122,0.1)' : '#fff', color: selected ? COLORS.green : COLORS.textBody, fontSize: 13, fontWeight: 500 }}>
                          {selected ? '✓ ' : '+ '}{role.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <ToggleRow label="Flag when total staff drops below a threshold" description="Get a soft reminder when the number of people scheduled at any one time falls below the count you set." value={coverage.minTotalEnabled} onChange={v => setCoverage(c => ({ ...c, minTotalEnabled: v }))} />
              {coverage.minTotalEnabled && (
                <div style={{ marginTop: 12, paddingLeft: 4 }}>
                  <label style={labelStyle}>Minimum total staff at any one time</label>
                  <input style={{ ...inputStyle, width: 200 }} type="number" min="1" placeholder="e.g. 3" value={coverage.minTotalCount} onChange={e => setCoverage(c => ({ ...c, minTotalCount: e.target.value }))} />
                  <p style={hintStyle}>Thrive will flag — not block — schedules below this count.</p>
                </div>
              )}
            </RuleCard>

            <RuleCard title="Coverage Patterns" description="Optional. Mark which days of the week run busier or slower than usual.">
              <ToggleRow label="Use peak / slow day patterns" description="If on, Thrive applies different reminder sensitivity on peak vs. slow days." value={coverage.patternsEnabled} onChange={v => setCoverage(c => ({ ...c, patternsEnabled: v }))} />

              {coverage.patternsEnabled && (
                <div style={{ marginTop: 18 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginBottom: 4 }}>Peak days <span style={{ color: COLORS.textMuted, fontWeight: 400, fontSize: 12 }}>(busier — flag understaffing more aggressively)</span></p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                    {DAYS.map(day => {
                      const selected = coverage.peakDays.includes(day);
                      return (
                        <button key={`peak-${day}`} onClick={() => setCoverage(c => ({ ...c, peakDays: selected ? c.peakDays.filter(d => d !== day) : [...c.peakDays.filter(d => !c.slowDays.includes(d)), day], slowDays: c.slowDays.filter(d => d !== day) }))} style={{ padding: '7px 16px', borderRadius: 100, cursor: 'pointer', border: `1px solid ${selected ? COLORS.greenBright : COLORS.border}`, background: selected ? 'rgba(34,197,122,0.1)' : '#fff', color: selected ? COLORS.green : COLORS.textBody, fontSize: 13, fontWeight: 500 }}>
                          {selected ? '✓ ' : ''}{day.slice(0, 3)}
                        </button>
                      );
                    })}
                  </div>

                  <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginBottom: 4 }}>Slow days <span style={{ color: COLORS.textMuted, fontWeight: 400, fontSize: 12 }}>(quieter — relaxed reminders)</span></p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {DAYS.map(day => {
                      const selected = coverage.slowDays.includes(day);
                      return (
                        <button key={`slow-${day}`} onClick={() => setCoverage(c => ({ ...c, slowDays: selected ? c.slowDays.filter(d => d !== day) : [...c.slowDays.filter(d => !c.peakDays.includes(d)), day], peakDays: c.peakDays.filter(d => d !== day) }))} style={{ padding: '7px 16px', borderRadius: 100, cursor: 'pointer', border: `1px solid ${selected ? COLORS.greenBright : COLORS.border}`, background: selected ? 'rgba(34,197,122,0.1)' : '#fff', color: selected ? COLORS.green : COLORS.textBody, fontSize: 13, fontWeight: 500 }}>
                          {selected ? '✓ ' : ''}{day.slice(0, 3)}
                        </button>
                      );
                    })}
                  </div>

                  {(coverage.peakDays.length > 0 || coverage.slowDays.length > 0) && (
                    <p style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 12 }}>💡 A day can be either peak or slow — not both. Days you don't mark stay neutral.</p>
                  )}
                </div>
              )}
            </RuleCard>

            <NavButtons onBack={() => setSection(7)} onNext={saveS8} nextLabel="Continue to Review →" saving={saving} />
          </div>
        )}

        {/* SECTION 9 */}
        {section === 9 && (
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.navy, margin: '0 0 8px', letterSpacing: '-0.03em' }}>Review your setup</h1>
            <p style={{ color: COLORS.textBody, fontSize: 15, marginBottom: 8, lineHeight: 1.6 }}>Here's everything you've set up. Review the summary below, then finish to start using Thrive AI.</p>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 32 }}>💡 You can change any of these settings later from your dashboard.</p>

            <RuleCard title="Your Organization" description="A quick summary of what you've set up. Use “Change” to jump back to any step.">
              {[
                { label: 'Organization name', value: orgName || 'Not set', target: 1 },
                { label: 'Roles', value: `${wizardRoles.length} ${wizardRoles.length === 1 ? 'role' : 'roles'}`, target: 2 },
                { label: 'Shifts', value: `${wizardShifts.length} ${wizardShifts.length === 1 ? 'shift' : 'shifts'}`, target: 3 },
                { label: 'Staff', value: `${wizardStaff.length} ${wizardStaff.length === 1 ? 'employee' : 'employees'}`, target: 4 },
              ].map((row, i) => (
                <div key={row.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderTop: i === 0 ? 'none' : `1px solid ${COLORS.border}` }}>
                  <div style={{ flex: 1, paddingRight: 20 }}>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 2 }}>{row.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.navy }}>{row.value}</div>
                  </div>
                  <button onClick={() => setSection(row.target)} style={{ background: 'none', border: 'none', color: COLORS.green, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '6px 10px', flexShrink: 0 }}>Change</button>
                </div>
              ))}
            </RuleCard>

            {finishError && (
              <div style={{ background: '#fee2e2', border: '1px solid #b91c1c', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>
                ❌ {finishError}
              </div>
            )}

            <NavButtons onBack={() => setSection(8)} onNext={saveS9} nextLabel="Finish Setup ✓" saving={saving} />
          </div>
        )}
      </div>
    </div>
  );
}
